import React = require('react');
import { ServiceLocator } from '../../app/ServiceLocator';
import { SessionManagementService, ErrorReason, getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { HelpService } from '../../commonInfra/services/HelpService';
import { AngularContextFactory } from '../../ts/factories/AngularContextFactory';
import { Messages } from '../../codegen/Localize';
import { ButtonType } from '@tableau/widgets/lib/src/widgets/Button/ButtonWidget';
import { ILongRunningAction } from '../../ts/models/ILongRunningAction';
import { ToasterService } from '../../ts/services/ToasterService';
import { ToastType, ButtonWidget, TextLinkWidget, TextLinkKind } from '@tableau/widgets';
import { L10n } from '../../ts/L10n/L10n';
import { BulkImportResultsSummaryProps, BulkImportResultsSummary } from './BulkImportResultsSummary';
import { FileInput } from './FileInput';
import { SessionService } from '../../../src/commonInfra/services/SessionService';
import { LongRunningAction } from '../../ts/services/LongRunningAction';
import { ISiteRolePickerMenuScope, SiteRolePickerMenuHelper } from '../../ts/helpers/SiteRolePickerMenuHelper';
import { AuthenticationProviderSelector } from '../../../src/settings/siteAuthSettings/directives/AuthenticationProviderSelector';
import { AuthenticationProviderSelectorModel } from '../../settings/siteAuthSettings/model/AuthenticationProviderSelectorModel';
import { IAddExternalUsersDialogScope } from './AddExternalSiteUsersDialog';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource} from '../services/resources/SiteRoleOptions';
import { CommonStyles } from '../../common/react/CommonStyles';
import { BulkImportUserDialogStyle } from './styles/BulkImportUserDialogStyle';
import WebHttpClient from '../../httpClient/WebHttpClient';

export type BulkImportUserDialogState = {
    inProgress: boolean;
    selectFile: boolean;
    importComplete: boolean;
    percentComplete: number;
    bulkImportResultsSummaryProps: BulkImportResultsSummaryProps;
    selectedFile: File;
};

export interface BulkImportUserDialogProps {
    readonly dismiss: () => void;
    readonly close: (result: any) => void;
    readonly method: string;
    readonly showAuthSettingSelector: boolean;
}

export interface IBulkImportUserDialogScope extends ISiteRolePickerMenuScope {

    authenticationProviderSelectorModel: AuthenticationProviderSelectorModel;
    siteRole: string;
}

export class BulkImportUserDialog extends React.Component<BulkImportUserDialogProps, BulkImportUserDialogState> {
    private dialogScope: ng.IScope = ServiceLocator.getAngularActionContext().scope;
    private endPoint: string = WebHttpClient.FILE_UPLOAD_API_PREFIX + this.props.method;
    // TODO This token should be in a common contextual help data structure in HelpService
    private csvHelpHref = HelpService.getServerHelpUrlByCategory('adminusers2');
    private angularContext = ServiceLocator.get<AngularContextFactory>('AngularContextFactory').create(this.dialogScope);
    private session: SessionService;
    private id = 0; // this is used to generate iframe-id and fileInput-id
    private siteRolesOptionResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;
    private translate = ServiceLocator.get<ng.translate.ITranslateService>('$translate');
    private server: SessionManagementService = getSessionManagementServiceInstance();
    private addUserScope = this.configureScope(this.dialogScope);
    private userAngularContext = ServiceLocator.get<AngularContextFactory>('AngularContextFactory').create(this.addUserScope);
    public constructor(props: BulkImportUserDialogProps) {
        super(props);
        this.state = {
            inProgress: false,
            selectFile: true,
            importComplete: false,
            percentComplete: 0,
            bulkImportResultsSummaryProps: undefined,
            selectedFile: undefined
        };
    }

    public render() {
        const importComplete = this.state.importComplete;
        return (
            <div className={BulkImportUserDialogStyle.importDialog}>
                <div>
                   {this.renderDescription()}
                </div>
                {this.renderAuthenticationProviderSelector(this.props.showAuthSettingSelector)}
                {
                    importComplete ? this.renderComponent() : this.renderForm()
                }
                {this.renderImportSummary()}
            </div>

        );
    }

    private renderDescription() {
        if(this.state.importComplete){
            return null;
        } else{
            return (<span>
                <p className={BulkImportUserDialogStyle.bodyText}>{Messages.uploadCSVFileContainUserName()}
                <TextLinkWidget
                kind = {TextLinkKind.Standard}
                testId='bulk-import-user-learn-more'
                href={this.csvHelpHref}
                target={'_blank'}
                className={BulkImportUserDialogStyle.bodyText}>
                {Messages.importUsers_LearnMoreAboutFileFormat()}
                </TextLinkWidget>
                </p>
            </span>);
        }
    }

    public handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        if (!this.state.inProgress) {
            event.preventDefault();
            let data = new FormData();
            data.append('XSRF-TOKEN', SessionService.getSessionId());
            data.append('reason', 'import');
            data.append('uploaded_file', this.state.selectedFile);
            data.append('authSetting', this.addUserScope.authenticationProviderSelectorModel.authSetting);
            this.setState({ inProgress: true, selectFile: false });
            fetch(this.endPoint, {
                method: 'POST',
                body: data,
            }).then(async res => {
                const result = await res.text();
                let domparser = new DOMParser();
                let doc = domparser.parseFromString(result, 'text/html');
                let data = doc.getElementById('json_response') as HTMLInputElement;
                let resultId = data.value;
                let id = JSON.parse(resultId).result.actionId;
                this.handleFormSubmitResult(id);
            });
        }
    };


    private renderAuthenticationProviderSelector(showAuthSettingSelector: boolean) {
        if (showAuthSettingSelector) {
            return (
                <AuthenticationProviderSelector model={this.addUserScope.authenticationProviderSelectorModel}/>
            );
        }
        return null;
    }

    private handleFormSubmitResult = (actionId: string) => {
        const notifyProgress = (action: ILongRunningAction) => {
            this.setState({ inProgress: true, percentComplete: action.percentComplete });
        };

        const importFailed = (reason: ErrorReason) => {
            this.setState({ inProgress: false, importComplete: true });
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.addUsersFailed() });
        };

        const importComplete = (action: ILongRunningAction) => {
            let bulkImportResultsSummaryProps = {
                bulkImportUsersResult: action.response,
            };
            this.setState({ inProgress: false, importComplete: true, percentComplete: action.percentComplete, bulkImportResultsSummaryProps: bulkImportResultsSummaryProps });
        };

        // the initial form submit result gives us an actionId that we use to poll for the real import results
        // we don't resolve the promise until we get import results or failure from the completed action.
        if (actionId) {
            LongRunningAction.trackAsyncJobProgress(actionId, importComplete, importFailed, notifyProgress);
        } else {
            importFailed(ErrorReason.Unknown);
        }
    };

    private handleFileInputChange = (fileInput: File) => {
        this.setState({selectedFile: fileInput});
    }

    private renderForm() {
        let authenticationProviderSelectorModel = new AuthenticationProviderSelectorModel();
        let authSetting = authenticationProviderSelectorModel.authSetting;
        const id = this.id++;
        return (
            <form className={BulkImportUserDialogStyle.importForm} onSubmit={this.handleSubmit.bind(this)} data-tb-test-id= 'bulkImportForm'>
                <FileInput
                    fileUploadId={'file-upload-' + id}
                    fileName={'uploaded_file'}
                    handleFileInput={this.handleFileInputChange}
                    testId='bulk-import-user-file'
                />
                <input type="hidden" name="authSetting" value={authSetting}></input>
                <input type="hidden" name="reason" value={'import'}></input>
                <input type="hidden" name="XSRF-TOKEN" value={SessionService.getSessionId()}></input>
                {this.renderComponent()}
            </form>);
    }

    private renderComponent() {
        return (<div className="tb-bulk-import-dialog-buttons tb-bottom-right">
            {this.renderPercentage()}
            {this.renderCancelButton()}
            {this.renderImportButton()}
        </div>);
    }

    private renderImportSummary() {
        const props = this.state.bulkImportResultsSummaryProps;
        if (props) {
            return (BulkImportResultsSummary(props));
        }
        return null;
    }

    private renderPercentage() {
        if (this.state.inProgress) {
            return (<span>
                {L10n.localizedPercent(this.state.percentComplete)}
            </span>);
        }
        return null;
    }

    private renderImportButton() {
        if (this.state.importComplete) {
            return null;
        }
        return (
            <span className="tb-margin-left-wide">
                <ButtonWidget
                  buttonType={ButtonType.Go}
                  type='submit'
                  testId={'bulk-import-user-dialog-import'}
                  disabled={this.state.inProgress || !this.state.selectedFile}>
                  {Messages.importUsers_Submit()}
                </ButtonWidget>
            </span>
        );

    }

    private renderCancelButton() {
        return (
            <span>
                <ButtonWidget
                    buttonType={ButtonType.Outline}
                    handleClick={this.state.importComplete ? this.props.close : this.props.dismiss}
                    testId={'confirm-action-dialog-cancel'}>
                    {this.state.importComplete ? Messages.done() : Messages.cancel()}
                </ButtonWidget>
            </span>
        );
    }


    public configureScope(dialogScope: ng.IScope): IBulkImportUserDialogScope {
        let addUserScope: IBulkImportUserDialogScope = dialogScope.$new() as IAddExternalUsersDialogScope;
        addUserScope.authenticationProviderSelectorModel = new AuthenticationProviderSelectorModel();
        addUserScope.siteRole = DefaultSiteRole;
        const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
            addUserScope.$apply(() => {
                addUserScope.siteRole = roleOption.role; // also set $scope.siteRole so original behavior is unaltered
                SiteRolePickerMenuHelper.setupPickerMenuScope(addUserScope, this.translate, this.server, this.siteRolesOptionResource, addUserScope.siteRole, onSiteRoleSelected, true);
            });
        };

        SiteRolePickerMenuHelper.setupPickerMenuScope(addUserScope, this.translate, this.server, this.siteRolesOptionResource, addUserScope.siteRole, onSiteRoleSelected, true);
        return addUserScope;
    }

}